import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Flex, Modal, TextField } from '@decisiv/ui-components';
import { P } from '@decisiv/ui-components/lib/components/Typography';
import Archive from '@decisiv/iconix/lib/components/Archive';
import { t, Trans } from '@lingui/macro';
import WrapperWithLoading from '../../components/WrapperWithLoading';

const sharedSettingShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  attributes: PropTypes.shape({ name: PropTypes.string }),
});

const ArchiveModal = ({
  visible,
  loading = false,
  integrationName,
  ssToDelete = [],
  ssToUpdate = [],
  onArchive,
  onClose,
}) => {
  const [confirmName, setConfirmName] = useState('');

  // Clear the confirmation input whenever the modal is opened or closed so a
  // stale value never carries over between sessions.
  useEffect(() => {
    setConfirmName('');
  }, [visible]);

  const nameMatches = confirmName.trim() === integrationName;

  return (
    <Modal
      actions={[
        {
          text: t`Archive Integration`,
          onClick: onArchive,
          intent: 'danger',
          disabled: !nameMatches,
        },
        { text: t`Cancel`, onClick: onClose },
      ]}
      icon={Archive}
      color="danger"
      onClose={onClose}
      title={t`Archive Integration`}
      visible={visible}
    >
      {loading ? (
        <WrapperWithLoading
          loading={loading}
          description={<Trans>Archiving Integration</Trans>}
        />
      ) : (
        <Flex flexDirection="column" marginTop={1}>
          <P shade={1}>
            <Trans>
              After clicking ARCHIVE INTEGRATION this integration will be put in
              the archive and no longer active. You can restore it from the
              archive page. Any locations in this integration will be disabled.
            </Trans>
          </P>

          {ssToDelete.length > 0 && (
            <Alert
              intent="warning"
              marginTop={1}
              title={`${ssToDelete.length} ${
                ssToDelete.length === 1
                  ? t`Shared Setting will be permanently deleted`
                  : t`Shared Settings will be permanently deleted`
              }`}
            >
              <Trans>
                This integration is the only link keeping the following Shared
                Settings valid. They will be deleted when this integration is
                archived:
              </Trans>
              <br />
              {ssToDelete.map((s) => (
                <React.Fragment key={s.id}>
                  <br />
                  &bull;&nbsp;{s.attributes?.name}
                </React.Fragment>
              ))}
            </Alert>
          )}

          {ssToUpdate.length > 0 && (
            <Alert
              intent="information"
              marginTop={1}
              title={`${ssToUpdate.length} ${
                ssToUpdate.length === 1
                  ? t`Shared Setting will be updated`
                  : t`Shared Settings will be updated`
              }`}
            >
              <Trans>
                The following Shared Settings reference this integration. This
                integration will be removed from their linked integrations list:
              </Trans>
              <br />
              {ssToUpdate.map((s) => (
                <React.Fragment key={s.id}>
                  <br />
                  &bull;&nbsp;{s.attributes?.name}
                </React.Fragment>
              ))}
            </Alert>
          )}

          {/* Require the user to type the integration name to confirm. The
              wrapper cancels paste/drop so the name must be typed (PD-3046). */}
          <div
            role="presentation"
            style={{ display: 'contents' }}
            onPaste={(e) => e.preventDefault()}
            onDrop={(e) => e.preventDefault()}
          >
            <TextField
              label={t`Type the integration name to confirm`}
              name="archive-confirm-name"
              autoComplete="off"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              helpMessage={integrationName}
              style={{ marginTop: '1rem' }}
            />
          </div>
        </Flex>
      )}
    </Modal>
  );
};

ArchiveModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  integrationName: PropTypes.string.isRequired,
  ssToDelete: PropTypes.arrayOf(sharedSettingShape),
  ssToUpdate: PropTypes.arrayOf(sharedSettingShape),
  onArchive: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
export default ArchiveModal;
